import type { AcademicTerm, Classroom } from "@classroom/domain";
import { scopeStudentRecords } from "@classroom/domain";
import { buildRecordReport, type RecordExportTab, type RecordReport } from "./recordExports";

export { encodeDelimitedSheet } from "./recordExportFormats";

export type ExportRange = { from?: string; to?: string };
export type RecordExportScope = {
  intent?: "print";
  report?: RecordReport;
  printReport?: RecordReport;
  range?: ExportRange;
  assignmentIds?: string[];
  studentIds?: string[];
  homeworkRecordIds?: string[];
  sheetTitles?: string[];
  label?: string;
};
export type RecordExportSource = Parameters<typeof buildRecordReport>[2];
export type ExportSheet = RecordReport["sheets"][number];
export type ExportErrorCode = "invalidDate" | "order" | "outsideTerm";
export class RecordExportError extends Error {
  constructor(readonly code: ExportErrorCode) {
    super(code);
  }
}

function validDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function validateExportRange(range: ExportRange, term?: AcademicTerm): ExportErrorCode | undefined {
  const { from, to } = range;
  if ((from && !validDate(from)) || (to && !validDate(to))) return "invalidDate";
  if (from && to && from > to) return "order";
  if (term && [from, to].some((date) => date && (date < term.startDate || date > term.endDate))) return "outsideTerm";
}

export function scopedExportSource(source: RecordExportSource, range: ExportRange, term?: AcademicTerm) {
  const error = validateExportRange(range, term);
  if (error) throw new RecordExportError(error);
  if (!term && !range.from && !range.to) return source;
  return scopeStudentRecords(source, {
    id: "export-range",
    schoolYear: "",
    name: "",
    startDate: range.from || term?.startDate || "0001-01-01",
    endDate: range.to || term?.endDate || "9999-12-31",
  });
}

export function buildScopedRecordReport(
  tab: RecordExportTab,
  classItem: Classroom,
  source: RecordExportSource,
  range: ExportRange,
  studentId?: string,
  term?: AcademicTerm,
): RecordReport {
  const report = buildRecordReport(tab, classItem, scopedExportSource(source, range, term), studentId, term);
  if (range.from || range.to) report.title += `｜${range.from || "…"}–${range.to || "…"}`;
  return report;
}
